// client/src/contexts/DashboardContext.tsx
"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { createMonitoredAxios, logCachedRequest } from '../utils/monitoredAxios';
import { postBatchWithRetry } from '../utils/batchRetry';
import { getAuthHeaders } from '../utils/authHeaders';
import { checkSessionAfter401 } from '../utils/sessionCheck';
import { capturePostHogEvent } from '@/analytics/posthog';
import axios from 'axios';
import { usePortfolio } from './PortfolioContext';
import { useScenarios } from './ScenarioContext';
import { useAuth } from './AuthContext';
import Cookies from 'js-cookie';
import { CashflowProjection } from '../components/CashflowTable';
import { Income, Expense, PortfolioSettings, HistoricValue, Category as FinancialCategory, FinancialSummaryData } from '../types/financial';
import { Recommendation } from '../types/recommendation';
import { CACHE_TTL, dashboardCache, getCacheKey, getCachedData, setCachedData, clearDashboardCache, clearCache } from './dashboardCache';
import { DashboardCacheListener } from './DashboardCacheListener';

// Re-export Category and HistoricValue for backward compatibility
export type Category = FinancialCategory;
export type { HistoricValue };

// Rate limiting for batch API calls
let lastBatchCallTime = 0;
const BATCH_CALL_THROTTLE = 100; // 200ms minimum between batch calls
 
let pendingBatchCall: Promise<unknown> | null = null;

export { clearDashboardCache };



import { Asset, Overview, AssetProjectionResponse, Transaction } from '@/types';
import type { ChartData } from 'chart.js';





interface DashboardContextType {
  overview: Overview | null;
  assetProjection: AssetProjectionResponse;
  cashflowProjection: CashflowProjection;
  recommendations: Recommendation[];
  financialSummary: FinancialSummaryData | null;
  assets: Asset[];
  categories: {
    asset: Category[];
    income: Category[];
    expense: Category[];
  };
  setCategories: (categories: {
    asset: Category[];
    income: Category[];
    expense: Category[];
  }) => void;
  transactions: Transaction[];
  income: Income[];
  expenses: Expense[];
  historicValues: HistoricValue[];
  portfolioSettings: PortfolioSettings | null;
  chartData: ChartData | null;
  loading: boolean;
  // True once batch data for the active portfolio has actually been applied.
  // `loading` alone can't answer this: it starts false and several paths clear
  // it without delivering data, so "not loading + no assets" also describes a
  // dashboard that simply hasn't fetched yet.
  dataLoaded: boolean;
  subtitle: string | null;
  error: string | null;
  includeDefault: boolean;
  projectionYears: number;
  setSubtitle: (subtitle: string) => Promise<void>;
  refreshDashboard: (scenarioId?: string | null, forceRefresh?: boolean) => Promise<void>;
  fetchOverview: (scenarioId?: string | null) => Promise<Overview | null>;
  fetchAssetProjection: (years?: number, scenarioId?: string | null) => Promise<AssetProjectionResponse>;
  fetchRecommendations: (scenarioId?: string | null) => Promise<Recommendation[]>;
  fetchFinancialSummary: (scenarioId?: string | null, years?: number) => Promise<FinancialSummaryData | null>;
  clearCache: (endpoint?: string, portfolioId?: string) => void;
  setIncludeDefault: (includeDefault: boolean) => void;
  setProjectionYears: (years: number) => void;
  setPortfolioSettings: (portfolioSettings: PortfolioSettings) => void;
  resetRetryState: () => void;
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

export function DashboardProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading, signOut } = useAuth();

  // A 401 from the dashboard batch used to sign the user out on the spot,
  // silently. Shortly after sign-up that can be a token that hasn't reached
  // this request yet (in-memory token lost on reload, cookie still being set)
  // rather than a dead session. Ask /api/auth/me first: only sign out when it
  // agrees the session is gone, and record which way it went either way.
  const handleBatch401 = (error: { config?: { url?: string } }) => {
    checkSessionAfter401()
      .then(({ stillSignedIn, meStatus }) => {
        if (stillSignedIn) {
          capturePostHogEvent('session_401_ignored', {
            source: 'dashboard_batch',
            url: error.config?.url,
          });
          return;
        }
        return signOut('api_401', { source: 'dashboard_batch', me_status: meStatus });
      })
      .catch(console.error);
  };
  const portfolioContext = usePortfolio();
  const activePortfolio = portfolioContext?.activePortfolio || null;
  // Use ScenarioContext for scenario state - unified across the app
  const { selectedScenario, setSelectedScenario } = useScenarios();
  const [overview, setOverview] = useState<Overview | null>(null);
  const [assetProjection, setAssetProjection] = useState<AssetProjectionResponse>({ projection: [] });
   
  const [cashflowProjection, setCashflowProjection] = useState<CashflowProjection>({} as CashflowProjection);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [financialSummary, setFinancialSummary] = useState<FinancialSummaryData | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loadedPortfolioId, setLoadedPortfolioId] = useState<string | null>(null);
  const [categories, setCategories] = useState<{
    asset: Category[];
    income: Category[];
    expense: Category[];
  }>({ asset: [], income: [], expense: [] });
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [income, setIncome] = useState<Income[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [historicValues, setHistoricValues] = useState<HistoricValue[]>([]);
  const [portfolioSettings, setPortfolioSettings] = useState<PortfolioSettings | null>(null);
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(false);

  // Use a ref to track auth loading state to avoid stale closure issues in setTimeout
  const authLoadingRef = useRef(authLoading);
  useEffect(() => {
    authLoadingRef.current = authLoading;
  }, [authLoading]);
  const [error, setError] = useState<string | null>(null);
  const [includeDefault, setIncludeDefault] = useState<boolean>(false);
  const [projectionYears, setProjectionYears] = useState<number>(10);
  const [subtitle, setSubtitle] = useState<string | null>(null);

  // Ref to track active portfolio to avoid circular dependencies in useCallback
  const activePortfolioRef = useRef(activePortfolio);
  useEffect(() => {
    activePortfolioRef.current = activePortfolio;
  }, [activePortfolio]);

  // Add refresh guard to prevent concurrent refreshes
  const isRefreshingRef = useRef(false);
  const lastRefreshTimeRef = useRef<number>(0);
  const portfolioRetryCountRef = useRef<number>(0);
  // Sticky: once retries are exhausted, stay given-up until a portfolio arrives.
  const portfolioRetriesExhaustedRef = useRef<boolean>(false);
  const portfolioRetryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastPortfolioFetchTimeRef = useRef<number>(0);
  // Request sequence to prevent stale responses from overwriting fresh ones
  const requestSequenceRef = useRef<number>(0);
  // ponytail: cancels the in-flight /api/batch when a new refresh starts, so a
  // scenario/portfolio switch doesn't reject the old request and trip the
  // error screen. axios.isCancel in the catch handles the abort cleanly.
  const batchAbortControllerRef = useRef<AbortController | null>(null);


  // Clear retry timer on component unmount or when portfolio is found
  useEffect(() => {
    return () => {
      if (portfolioRetryTimerRef.current) {
        clearTimeout(portfolioRetryTimerRef.current);
        portfolioRetryTimerRef.current = null;
      }
    };
  }, []);

  // Reset retry count and clear error when portfolio is found
  useEffect(() => {
    if (activePortfolio && portfolioRetryCountRef.current > 0) {
      console.log('[Dashboard] Portfolio found, resetting retry count and clearing error');
      portfolioRetryCountRef.current = 0;
      portfolioRetriesExhaustedRef.current = false;
      if (portfolioRetryTimerRef.current) {
        clearTimeout(portfolioRetryTimerRef.current);
        portfolioRetryTimerRef.current = null;
      }
      setError(null); // Clear any error from failed retries
    }

    // Update the timestamp whenever we have an active portfolio
    if (activePortfolio) {
      lastPortfolioFetchTimeRef.current = Date.now();
    }
  }, [activePortfolio]);

  // Wrapper function to match interface requirement
  const handleSetSubtitle = async (subtitle: string): Promise<void> => {
    setSubtitle(subtitle);
  };

  const fetchComprehensiveBatchData = useCallback(async (scenarioId: string | null = null, forceRefresh: boolean = false) => {

    if (!scenarioId) {
      scenarioId = selectedScenario
    }

    console.log('[Dashboard] fetchComprehensiveBatchData called with scenarioId:', scenarioId, 'selectedScenario from context:', selectedScenario);

    // Increment request sequence to track this request
    const currentSequence = ++requestSequenceRef.current;
    console.log('[Dashboard] Request sequence:', currentSequence, 'scenarioId:', scenarioId, 'forceRefresh:', forceRefresh);

    const years = 50;
    const currentActivePortfolio = activePortfolioRef.current;
    if (!currentActivePortfolio) {
      setLoading(false);
      return null;
    }

    if (subtitle === null) {
      setSubtitle("Financial clarity. Calculated.");
    }

    // Check if user is authenticated before proceeding
    // With SSR auth data, user should be available immediately
    if (!user) {
      console.log('[Dashboard] No user available for dashboard refresh - user:', user, 'authLoading:', authLoading);
      setLoading(false);
      return null;
    }

    // Rate limiting: check if we have a pending call or need to wait
    const now = Date.now();
    const timeSinceLastCall = now - lastBatchCallTime;

    // If forceRefresh is true, always make a fresh call - bypass pending calls and throttle
    if (!forceRefresh) {
      // If there's a pending call, return it (only when not force refreshing)
      if (pendingBatchCall) {
        return pendingBatchCall;
      }

      // If not enough time has passed, wait for the remaining time
      if (timeSinceLastCall < BATCH_CALL_THROTTLE) {
        const waitTime = BATCH_CALL_THROTTLE - timeSinceLastCall;
        console.log(`[Dashboard] Throttling batch call, waiting ${waitTime}ms`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    }

    console.log('[Dashboard] Making API call with forceRefresh:', forceRefresh);
    try {
      // Always include scenario_id in params for proper cache key differentiation
      // Use 'null' string if no scenario to ensure cache keys differ
      const params: { scenario_id: string | null; include_default?: string; years?: number } = {
        scenario_id: scenarioId,  // Always include scenario_id (even if null)
      };
      if (includeDefault) params.include_default = 'true';
      params.years = years;

      // Check cache first
      const cacheKey = getCacheKey('/api/batch', { ...params, years }, currentActivePortfolio.id);
      const cachedData = getCachedData(cacheKey);
      if (cachedData && !forceRefresh) {
        logCachedRequest('/api/batch', 'POST', 'DashboardContext');
        setLoading(false);
        return { data: cachedData, sequence: currentSequence };
      }

      // ponytail: cancel any previous request so a fast scenario/portfolio switch
      // doesn't reject as AxiosError: Network Error and trip the error screen.
      //
      // This must stay here, below the cache hit and the `pendingBatchCall`
      // reuse above, and not at the top of the function. Aborting on entry
      // killed the in-flight request that a non-forceRefresh caller was about
      // to return as `pendingBatchCall`, so both the original and the reusing
      // call resolved to a cancel and no batch response was ever applied -
      // leaving categories, assets and income empty on every page. Only a call
      // that is genuinely about to issue its own request may supersede the
      // one in flight.
      if (batchAbortControllerRef.current) {
        batchAbortControllerRef.current.abort();
      }
      const abortController = new AbortController();
      batchAbortControllerRef.current = abortController;

      // Update last call time before making the request
      lastBatchCallTime = Date.now();

      const monitoredAxios = createMonitoredAxios('DashboardContext');

      // Create the batch call promise (retries transient failures with backoff)
      const batchCallPromise = postBatchWithRetry<{ success: boolean; results: Record<string, unknown>; errors?: Record<string, unknown> }>(monitoredAxios, '/api/batch', {
        requests: [
          // Authoritative data sources (duplicates removed)
          {
            id: 'comprehensive',
            endpoint: '/dashboard/comprehensive',
            method: 'GET',
            params
          },

          {
            id: 'recommendations',
            endpoint: '/dashboard/recommendations',
            method: 'GET',
            params
          },
          {
            id: 'cashflow-projection',
            endpoint: '/dashboard/cashflow-projection',
            method: 'GET',
            params
          },
          {
            id: 'asset-categories',
            endpoint: '/assets/categories',
            method: 'GET',
            params
          },
          {
            id: 'income-categories',
            endpoint: '/income-categories',
            method: 'GET',
            params
          },
          {
            id: 'expense-categories',
            endpoint: '/expense-categories',
            method: 'GET',
            params
          },
          {
            id: 'portfolio-settings',
            endpoint: '/portfolio-settings',
            method: 'GET',
            params
          },
          {
            id: 'chart-data',
            endpoint: '/dashboard/chart-data',
            method: 'GET',
            params
          },
          {
            id: 'income',
            endpoint: '/income',
            method: 'GET',
            params
          },
          {
            id: 'assets',
            endpoint: '/assets',
            method: 'GET',
            params
          },
          {
            id: 'expenses',
            endpoint: '/expenses',
            method: 'GET',
            params
          },
          {
            id: 'transactions',
            endpoint: '/transactions',
            method: 'GET',
            params
          },
          {
            id: 'financial-summary',
            endpoint: '/dashboard/financial-summary',
            method: 'GET',
            params
          },
          {
            id: 'asset-projection',
            endpoint: '/dashboard/asset-projection',
            method: 'GET',
            params
          },

          {
            id: 'historic-values',
            endpoint: '/assets/historic-values/all',
            method: 'GET',
            params
          }
        ]
      }, {
        headers: getAuthHeaders(currentActivePortfolio.id),
        timeout: 30000, // Increased timeout for comprehensive request
        signal: abortController.signal,
      });

      // Store the pending promise
      pendingBatchCall = batchCallPromise.then(response => {
        pendingBatchCall = null; // Clear pending call when complete
        return response;
      }).catch(error => {
        pendingBatchCall = null; // Clear pending call on error
        if (error.response?.status === 401) {
          handleBatch401(error);
        }

        // ponytail: callers that picked up this `pendingBatchCall` from line
        // 213 above await it fire-and-forget (the dashboard mount timer in
        // particular). If we re-throw the abort, their returned promise
        // rejects with CanceledError and PostHog's exception autocapture
        // picks up the unhandled rejection - "CanceledError: canceled"
        // with no real [IN-APP] frames, one alert per scenario switch.
        // The newer request owns the UI from here; resolve with null and let
        // the outer await + axios.isCancel branch handle it normally.
        if (axios.isCancel(error)) {
          return null;
        }

        throw error;
      });

      const response = await batchCallPromise;



      // Check for any errors in the batch response
      if (response.data.errors && Object.keys(response.data.errors).length > 0) {
        console.warn('[Dashboard] Some batch requests failed:', response.data.errors);
        if (response.data.errors['cashflow-projection']) {
          console.error('[Dashboard] Cashflow projection error:', response.data.errors['cashflow-projection']);
        }
      }

      const batchData = response.data.results as Record<string, unknown>;
      // Cache the comprehensive response
      setCachedData(cacheKey, batchData, CACHE_TTL.COMPREHENSIVE);

      setAssets((batchData.assets as Asset[]) || []);
      setLoadedPortfolioId(currentActivePortfolio.id);
      setIncome((batchData.income as Income[]) || []);
      setExpenses((batchData.expenses as Expense[]) || []);
      setTransactions((batchData.transactions as Transaction[]) || []);
      setAssetProjection((batchData['asset-projection'] as AssetProjectionResponse) || { projection: [] });
      setCashflowProjection((batchData['cashflow-projection'] as CashflowProjection) || {} as CashflowProjection);
      setHistoricValues((batchData['historic-values'] as HistoricValue[]) || []);
      setOverview((batchData.comprehensive as { overview: Overview })?.overview || null);
      setRecommendations((batchData.recommendations as Recommendation[]) || []);
      setFinancialSummary((batchData['financial-summary'] as FinancialSummaryData) || null);
      setPortfolioSettings((batchData['portfolio-settings'] as PortfolioSettings) || null);
      setChartData((batchData['chart-data'] as ChartData) || null);

      // Combine all category types
      const allCategories = {
        asset: (batchData['asset-categories'] as Category[]) || [],
        income: (batchData['income-categories'] as Category[]) || [],
        expense: (batchData['expense-categories'] as Category[]) || []
      };

      setCategories(allCategories);
      setLoading(false);

      console.log('[Dashboard] Returning batchData with sequence:', currentSequence);
      return { data: batchData, sequence: currentSequence };

    } catch (error) {
      // ponytail: cancelled requests are not errors. The new scenario/portfolio
      // request owns the UI from here; surfacing the abort would clear the
      // dashboard for no reason.
      if (axios.isCancel(error)) {
        console.log('[Dashboard] Batch request cancelled');
        return null;
      }
      console.error('[Dashboard] Error in fetchComprehensiveBatchData:', error);
      setLoading(false);

      throw error;
    }

    // Reset retry count when we have an active portfolio
    if (portfolioRetryCountRef.current > 0) {
      console.log('[Dashboard] Active portfolio found, resetting retry count');
      portfolioRetryCountRef.current = 0;
      portfolioRetriesExhaustedRef.current = false;
      if (portfolioRetryTimerRef.current) {
        clearTimeout(portfolioRetryTimerRef.current!);
        portfolioRetryTimerRef.current = null;
      }
      setError(null); // Clear any error from failed retries
    }

    // Additional safety check: if we had an active portfolio but it's now null, and we're not in OAuth setup, this might be a state issue
    if (!currentActivePortfolio && selectedScenario && user && !authLoading) {
      console.log('[Dashboard] Warning: Active portfolio is null but scenario is selected. This might be a state issue.');
      console.log('[Dashboard] Current state:', { activePortfolio: !!currentActivePortfolio, selectedScenario, user: !!user, authLoading });

      // Check if we're in OAuth setup before taking action
      const newDefaultScenarioId = localStorage.getItem('new_default_scenario_id');
      const forceRefresh = localStorage.getItem('force_dashboard_refresh');
      const isOAuthSetupInProgress = !!(newDefaultScenarioId || forceRefresh);

      if (!isOAuthSetupInProgress) {
        console.log('[Dashboard] Not in OAuth setup, this might be a genuine state issue');
      } else {
        console.log('[Dashboard] OAuth setup detected, portfolio state will be restored');
      }
    }
  }, [includeDefault, projectionYears, selectedScenario, user, authLoading]);

  const refreshDashboard = useCallback(async (scenarioId: string | null = null, forceRefresh: boolean = false) => {
    if (!scenarioId) {
      scenarioId = selectedScenario
    }

    if (!activePortfolio) {
      // If user is logged in but no portfolio, start retry mechanism
      if (user && !authLoading) {
        console.log('[Dashboard] User logged in but no portfolio - checking if OAuth setup in progress');

        // Check if we're in the middle of OAuth setup to avoid interfering
        const newDefaultScenarioId = localStorage.getItem('new_default_scenario_id');
        const forceRefresh = localStorage.getItem('force_dashboard_refresh');
        const newPortfolioId = localStorage.getItem('new_portfolio_id');
        const oauthSetupInProgress = localStorage.getItem('oauth_setup_in_progress');
        const isOAuthSetupInProgress = !!(newDefaultScenarioId || forceRefresh || newPortfolioId || oauthSetupInProgress);

        // Additional check: if we recently had a successful portfolio fetch, don't start retry
        const timeSinceLastPortfolioFetch = Date.now() - (lastPortfolioFetchTimeRef.current || 0);
        const recentlyHadPortfolio = timeSinceLastPortfolioFetch < 5000; // 5 seconds

        if (isOAuthSetupInProgress || recentlyHadPortfolio) {
          console.log('[Dashboard] OAuth setup in progress or recently had portfolio, skipping retry mechanism');
          console.log('[Dashboard] Skip reason:', { isOAuthSetupInProgress, recentlyHadPortfolio, timeSinceLastPortfolioFetch, oauthSetupInProgress });
          setLoading(false);
          return;
        }

        console.log('[Dashboard] User logged in but no portfolio - starting retry mechanism');
        setLoading(true); // Show loading spinner

        // Start retry mechanism if not already running
        if (portfolioRetryCountRef.current === 0) {
          portfolioRetryCountRef.current++;
          console.log(`[Dashboard] Portfolio retry ${portfolioRetryCountRef.current}/5 - retrying in 500ms`);

          portfolioRetryTimerRef.current = setTimeout(() => {
            refreshDashboard(scenarioId, true); // Force refresh to retry portfolio fetch
          }, 500);
        } else if (portfolioRetryCountRef.current < 5) {
          // Continue retry mechanism
          portfolioRetryCountRef.current++;
          console.log(`[Dashboard] Portfolio retry ${portfolioRetryCountRef.current}/5 - retrying in 500ms`);

          portfolioRetryTimerRef.current = setTimeout(() => {
            refreshDashboard(scenarioId, true); // Force refresh to retry portfolio fetch
          }, 500);
        } else {
          // Do NOT reset the counter here. Resetting it restored the `=== 0`
          // precondition above, so the next run of this effect started the
          // five-retry cycle again - giving up, then immediately trying again,
          // forever. The counter stays exhausted until a portfolio actually
          // arrives, which is what the reset effects above are for.
          if (!portfolioRetriesExhaustedRef.current) {
            console.log('[Dashboard] Portfolio retries exhausted (5 attempts), giving up');
            portfolioRetriesExhaustedRef.current = true;
            setError('Unable to load portfolios. Please refresh the page or contact support.');
          }
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
      return;
    }

    // Prevent concurrent refreshes, but allow force refreshes to bypass
    if (isRefreshingRef.current && !forceRefresh) {
      console.log('[Dashboard] Refresh already in progress, skipping...');
      return;
    }

    // Additional guard: prevent multiple refreshes within short time
    const currentTime = Date.now();
    if (lastRefreshTimeRef.current && (currentTime - lastRefreshTimeRef.current < 1000) && !forceRefresh) {
      return;
    }
    lastRefreshTimeRef.current = currentTime;
    isRefreshingRef.current = true;
    setLoading(true);
    setError(null);

    try {

      if (forceRefresh) {
        // Clear all cache
        dashboardCache.clear();
      }

      // Use the comprehensive batch function to get all data
      const result = await fetchComprehensiveBatchData(scenarioId, forceRefresh) as
        | { data: Record<string, unknown> | null; sequence: number }
        | null;

      // ponytail: a null result means the fetch bailed out rather than failed -
      // the request was aborted by a newer refresh, or there was no portfolio or
      // user to fetch for. The old cast claimed the result was never null, so
      // reading .sequence threw a TypeError that the catch below could not
      // recognise as a cancel, and every aborted refresh painted the "We could
      // not load your dashboard" error screen. A force refresh bypasses the
      // concurrency guard above, so this fires on any new-user dashboard where
      // the portfolio retry overlaps the first load. Not an error state: the
      // newer request owns the UI from here.
      if (!result) {
        console.log('[Dashboard] Refresh superseded or skipped - nothing to apply');
        return;
      }

      // Check if this response is still valid (not superseded by a newer request)
      if (result.sequence !== requestSequenceRef.current) {
        console.log('[Dashboard] Discarding stale response with sequence:', result.sequence, 'current sequence:', requestSequenceRef.current);
        return;
      }

      // Handle missing auth token
      if (!result.data) {
        console.log('[Dashboard] No data returned - likely not authenticated');
        setLoading(false);
        isRefreshingRef.current = false;
        return;
      }

      console.log('[Dashboard] Using response with sequence:', result.sequence, 'scenario from overview:', (result.data.comprehensive as any)?.overview?.totalAnnualIncome);

      const batchData = result.data;

      // Update all state with the fetched data (fix for refreshDashboard not updating state)

      // Transform batchData.categories to the same structure as initial load
      let allCategories = {
        asset: [] as Category[],
        income: [] as Category[],
        expense: [] as Category[]
      };
      try {
        allCategories = {
          asset: (batchData['asset-categories'] as Category[]) || [],
          income: (batchData['income-categories'] as Category[]) || [],
          expense: (batchData['expense-categories'] as Category[]) || []
        };

        setOverview(((batchData.comprehensive as { overview: Overview })?.overview) || null);
        setAssets((batchData.assets as Asset[]) || []);
        setLoadedPortfolioId(activePortfolioRef.current?.id ?? null);
        setIncome((batchData.income as Income[]) || []);
        setExpenses((batchData.expenses as Expense[]) || []);
        setTransactions((batchData.transactions as Transaction[]) || []);
        setCategories(allCategories);
        setHistoricValues((batchData['historic-values'] as HistoricValue[]) || []);
        setRecommendations((batchData.recommendations as Recommendation[]) || []);
      } catch (_e) {
        alert("ERROR GETTING DASHBOARD DATA");


      }

    } catch (err: unknown) {
      // ponytail: cancelled requests aren't errors. The newer request will
      // own the UI; don't blank the dashboard on a programmatic cancel.
      if (axios.isCancel(err)) {
        console.log('[Dashboard] Refresh cancelled');
        return;
      }
      console.error('[Dashboard] Refresh error:', err);
      // Don't leak the raw axios/network string - it can contain URLs,
      // internal IP:port pairs, and bearer-token fragments. The dashboard's
      // recovery surface is "Sign out and start a new session", so the user
      // doesn't need a precise cause; a stable phrasing keeps support tickets
      // anonymous and prevents fingerprinting from session replay noise.
      const status = (err as { response?: { status?: number } } | null)?.response?.status;
      const message =
        status === 401 ? 'Your session has expired.'
        : status === 431 ? 'Your session is too large to send. Sign out and back in to start fresh.'
        : status === 403 ? 'You do not have access to this portfolio.'
        : status === 404 ? 'Dashboard data is temporarily unavailable.'
        : status && status >= 500 ? 'Something went wrong on our end. Please try again.'
        : 'We could not load your dashboard. Sign out and back in to start fresh.';
      setError(message);
    } finally {
      setLoading(false);
      isRefreshingRef.current = false; // Reset refresh guard
      // Reset the refresh time guard after a delay to allow immediate next refresh if needed
      setTimeout(() => {
        lastRefreshTimeRef.current = 0;
      }, 2000);
    }
  }, [activePortfolio, user, authLoading, fetchComprehensiveBatchData, selectedScenario]);

  // Function to handle portfolio retry with exponential backoff and 401 detection
  const handlePortfolioRetry = useCallback(() => {
    if (portfolioRetryCountRef.current < 5) {
      portfolioRetryCountRef.current++;
      // Exponential backoff: 500ms, 1000ms, 2000ms, 4000ms
      const delay = 500 * Math.pow(2, portfolioRetryCountRef.current - 1);
      console.log(`[Dashboard] Portfolio retry ${portfolioRetryCountRef.current}/5 - retrying in ${delay}ms`);

      portfolioRetryTimerRef.current = setTimeout(() => {
        refreshDashboard(null, true); // Force refresh to retry portfolio fetch
      }, delay);
    } else {
      // Same fix as the other retry path above: leave the counter exhausted
      // instead of resetting it to 0, or this immediately restarts the cycle.
      if (!portfolioRetriesExhaustedRef.current) {
        console.log('[Dashboard] Portfolio retries exhausted (5 attempts), giving up');
        portfolioRetriesExhaustedRef.current = true;
        setError('Unable to load portfolios. Please refresh the page or contact support.');
      }
      setLoading(false);
    }
  }, [refreshDashboard]);

  // ponytail: wire-up for the "Try Again" button on the error screen. Without
  // this the retry refs stay latched and the user has to sign out to escape.
  const resetRetryState = useCallback(() => {
    portfolioRetryCountRef.current = 0;
    portfolioRetriesExhaustedRef.current = false;
    if (portfolioRetryTimerRef.current) {
      clearTimeout(portfolioRetryTimerRef.current);
      portfolioRetryTimerRef.current = null;
    }
    setError(null);
  }, []);

  // Clear retry timer on component unmount or when portfolio is found
  useEffect(() => {
    return () => {
      if (portfolioRetryTimerRef.current) {
        clearTimeout(portfolioRetryTimerRef.current);
        portfolioRetryTimerRef.current = null;
      }
    };
  }, []);

  // Reset retry count and clear error when portfolio is found
  useEffect(() => {
    if (activePortfolio && portfolioRetryCountRef.current > 0) {
      console.log('[Dashboard] Portfolio found, resetting retry count and clearing error');
      portfolioRetryCountRef.current = 0;
      portfolioRetriesExhaustedRef.current = false;
      if (portfolioRetryTimerRef.current) {
        clearTimeout(portfolioRetryTimerRef.current);
        portfolioRetryTimerRef.current = null;
      }
      setError(null); // Clear any error from failed retries
    }

    // Update the timestamp whenever we have an active portfolio
    if (activePortfolio) {
      lastPortfolioFetchTimeRef.current = Date.now();
    }
  }, [activePortfolio]);


  // Handle case where user is logged in but no portfolios are available
  useEffect(() => {
    // Check if we're in the middle of OAuth setup to avoid interfering
    const newDefaultScenarioId = localStorage.getItem('new_default_scenario_id');
    const forceRefresh = localStorage.getItem('force_dashboard_refresh');
    const isOAuthSetupInProgress = !!(newDefaultScenarioId || forceRefresh);

    // Also check if we recently had a successful refresh (within last 2 seconds)
    const timeSinceLastRefresh = lastRefreshTimeRef.current ? Date.now() - lastRefreshTimeRef.current : Infinity;
    const recentlyRefreshed = timeSinceLastRefresh < 2000;

    // Additional check: if we had a selected scenario recently, we probably had a portfolio
    const recentlySelectedScenario = selectedScenario && timeSinceLastRefresh < 3000;

    // More patient initialization - wait for auth to fully settle and portfolio context to initialize
    // Don't start retry immediately - give portfolio context time to fetch and set portfolios
    const shouldRetry = user &&
      !authLoading &&
      !activePortfolio &&
      !loading &&
      portfolioRetryCountRef.current === 0 &&
      !isOAuthSetupInProgress &&
      !recentlyRefreshed &&
      !recentlySelectedScenario;

    if (shouldRetry) {
      console.log('[Dashboard] User logged in but no portfolio available - starting initial retry');
      console.log('[Dashboard] Retry conditions check:', {
        user: !!user,
        authLoading,
        activePortfolio: !!activePortfolio,
        loading,
        portfolioRetryCount: portfolioRetryCountRef.current,
        isOAuthSetupInProgress,
        recentlyRefreshed,
        recentlySelectedScenario,
        timeSinceLastRefresh
      });
      setLoading(true);
      handlePortfolioRetry();
    } else if (user && !authLoading && !activePortfolio && !loading && portfolioRetryCountRef.current === 0 && (recentlyRefreshed || recentlySelectedScenario)) {
      console.log('[Dashboard] User logged in but no portfolio - skipping retry due to recent activity');
      console.log('[Dashboard] Skip reason:', { recentlyRefreshed, recentlySelectedScenario, timeSinceLastRefresh });
    }
  }, [user, authLoading, activePortfolio, loading, handlePortfolioRetry, selectedScenario]);

  // Refresh dashboard when active portfolio or scenario changes (but not during OAuth setup or auth settling)
  useEffect(() => {
    const activePortfolioId = activePortfolio?.id || null;
    console.log('[Dashboard] useEffect triggered:', {
      activePortfolioId,
      hasUser: !!user,
      authLoading,
      selectedScenario,
      hasSelectedScenario: !!selectedScenario
    });

    // More patient check - wait for auth to settle and portfolio to be stable
    if (authLoading) {
      console.log('[Dashboard] Skipping refresh - auth still loading');
      return;
    }

    if (!user) {
      console.log('[Dashboard] Skipping refresh - no user');
      return;
    }

    if (!activePortfolio) {
      console.log('[Dashboard] Skipping refresh - no active portfolio');
      return;
    }

    if (!selectedScenario) {
      console.log('[Dashboard] Skipping refresh - no selected scenario');
      return;
    }

    // Check if we're in middle of OAuth setup to avoid duplicate refreshes
    const newDefaultScenarioId = localStorage.getItem('new_default_scenario_id');
    const forceRefresh = localStorage.getItem('force_dashboard_refresh');
    const oauthSetupInProgress = localStorage.getItem('oauth_setup_in_progress');

    // Clear oauthSetupInProgress if we have a valid selectedScenario - this allows
    // the dashboard to refresh properly after scenario switch even if OAuth was in progress
    if (selectedScenario && oauthSetupInProgress) {
      console.log('[Dashboard] Clearing oauthSetupInProgress flag since we have a selectedScenario');
      localStorage.removeItem('oauth_setup_in_progress');
    }

    console.log('[Dashboard] OAuth flags:', {
      newDefaultScenarioId,
      forceRefresh,
      oauthSetupInProgress
    });

    if (!newDefaultScenarioId && !forceRefresh && !oauthSetupInProgress) {
      console.log('[Dashboard] REFRESHING DASHBOARD on portfolio/scenario change for portfolio:', activePortfolioId);
      console.log('[Dashboard] Selected scenario:', selectedScenario);
      refreshDashboard(selectedScenario, true); // Always force refresh when portfolio changes
    } else {
      console.log('[Dashboard] Skipping refresh - OAuth setup in progress');
    }
  }, [selectedScenario, activePortfolio, user, authLoading, refreshDashboard]);

  // Ref to track last checked welcome screen flags to avoid unnecessary checks
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const newAssetAdded = localStorage.getItem('new_asset_added');
      const forceRefresh = localStorage.getItem('force_dashboard_refresh');
      const newDefaultScenarioId = localStorage.getItem('new_default_scenario_id');

      console.log('[Dashboard] Checking flags:', {
        newAssetAdded,
        forceRefresh,
        newDefaultScenarioId,
        activePortfolio: !!activePortfolio,
        user: !!user,
        authLoading,
        currentSelectedScenario: selectedScenario
      });

      // Only process flags if we have an active portfolio
      if ((newAssetAdded === 'true' || forceRefresh === 'true' || newDefaultScenarioId) && activePortfolio) {

        console.log('[Dashboard] Flags detected with active portfolio, triggering force refresh');

        // If we have a new default scenario, set it as the selected scenario
        if (newDefaultScenarioId && newDefaultScenarioId !== selectedScenario) {
          console.log('[Dashboard] Setting new default scenario:', newDefaultScenarioId);
          console.log('[Dashboard] Previous scenario was:', selectedScenario);
          setSelectedScenario(newDefaultScenarioId);
          console.log('[Dashboard] Scenario set to:', newDefaultScenarioId);
        } else if (newDefaultScenarioId && newDefaultScenarioId === selectedScenario) {
          console.log('[Dashboard] Default scenario already selected, skipping scenario change');
        }

        // Force refresh the dashboard to pick up the new portfolio and scenario
        if (activePortfolio && user && !authLoading) {
          console.log('[Dashboard] Calling refreshDashboard with forceRefresh=true');
          const scenarioToUse = newDefaultScenarioId || selectedScenario;
          console.log('[Dashboard] Using scenario for refresh:', scenarioToUse);
          refreshDashboard(scenarioToUse, true); // Force refresh

          // Clear the flags AFTER the refresh is initiated, not before
          setTimeout(() => {
            console.log('[Dashboard] Clearing OAuth flags after refresh initiated');
            localStorage.removeItem('new_asset_added');
            localStorage.removeItem('new_asset_id');
            localStorage.removeItem('new_asset_timestamp');
            localStorage.removeItem('force_dashboard_refresh');
            localStorage.removeItem('refresh_timestamp');
            localStorage.removeItem('new_default_scenario_id');
            localStorage.removeItem('new_default_scenario_name');
            localStorage.removeItem('new_portfolio_id');
            localStorage.removeItem('new_portfolio_name');
          }, 100); // Small delay to ensure refresh starts
        } else {
          console.log('[Dashboard] Cannot refresh - missing required data:', { activePortfolio: !!activePortfolio, user: !!user, authLoading });
          // Clear flags even if we can't refresh to prevent infinite loops
          localStorage.removeItem('new_asset_added');
          localStorage.removeItem('new_asset_id');
          localStorage.removeItem('new_asset_timestamp');
          localStorage.removeItem('force_dashboard_refresh');
          localStorage.removeItem('refresh_timestamp');
          localStorage.removeItem('new_default_scenario_id');
          localStorage.removeItem('new_default_scenario_name');
          localStorage.removeItem('new_portfolio_id');
          localStorage.removeItem('new_portfolio_name');
        }
      } else if ((newAssetAdded === 'true' || forceRefresh === 'true' || newDefaultScenarioId) && !activePortfolio) {
        console.log('[Dashboard] Flags detected but no active portfolio, waiting...');
      }
    }
  }, [activePortfolio, selectedScenario, user, authLoading, refreshDashboard, setSelectedScenario]);

  // Clear cache when user logs out (but not on initial render)
  const previousUserRef = useRef(user);
  useEffect(() => {
    const previousUser = previousUserRef.current;
    previousUserRef.current = user;

    // Only clear cache if user had a value before and now doesn't (actual logout)
    if (previousUser && !user) {
      console.log('[Dashboard] User logged out, clearing cache');
      clearCache();
    }
  }, [user]);

  // Individual fetch functions for specific data types

  // fetchComprehensiveBatchData resolves to { data, sequence } - or null when
  // the request was superseded or there was nothing to fetch. The fetchers
  // below want the batch results themselves, and each previously read
  // `batchData.comprehensive` etc. straight off that wrapper, which is always
  // undefined, so they silently returned null/[] no matter what came back.
  const fetchBatchResults = useCallback(async (
    scenarioId: string | null = null
  ): Promise<Record<string, unknown> | null> => {
    const result = await fetchComprehensiveBatchData(scenarioId) as
      | { data: Record<string, unknown> | null; sequence: number }
      | null;
    return result?.data ?? null;
  }, [fetchComprehensiveBatchData]);

  const fetchOverview = useCallback(async (scenarioId: string | null = null) => {
    if (!activePortfolio) return null;

    try {
      const batchData = await fetchBatchResults(scenarioId);
      return ((batchData?.comprehensive as { overview: Overview })?.overview) || null;
    } catch (error) {
      console.error('[Dashboard] fetchOverview error:', error);
      return null;
    }
  }, [activePortfolio, fetchBatchResults]);

  const fetchAssetProjection = useCallback(async (_years: number = 30, scenarioId: string | null = null): Promise<AssetProjectionResponse> => {
    if (!activePortfolio) return { projection: [] };

    try {
      const batchData = await fetchBatchResults(scenarioId);
      return (batchData?.['asset-projection'] as AssetProjectionResponse) || { projection: [] };
    } catch (error) {
      console.error('[Dashboard] fetchAssetProjection error:', error);
      return { projection: [] };
    }
  }, [activePortfolio, fetchBatchResults]);

  const fetchRecommendations = useCallback(async (scenarioId: string | null = null) => {
    if (!activePortfolio) return [];

    try {
      const batchData = await fetchBatchResults(scenarioId);
      return (batchData?.recommendations as Recommendation[]) || [];
    } catch (error) {
      console.error('[Dashboard] fetchRecommendations error:', error);
      return [];
    }
  }, [activePortfolio, fetchBatchResults]);

  const fetchFinancialSummary = useCallback(async (scenarioId: string | null = null, _years: number = 30) => {
    if (!activePortfolio) return null;

    try {
      const batchData = await fetchBatchResults(scenarioId);
      return (batchData?.['financial-summary'] as FinancialSummaryData) || null;
    } catch (error) {
      console.error('[Dashboard] fetchFinancialSummary error:', error);
      return null;
    }
  }, [activePortfolio, fetchBatchResults]);

  // Enhanced setPortfolioSettings function that also updates the server
  const updatePortfolioSettings = useCallback(async (newSettings: PortfolioSettings) => {
    if (!activePortfolio?.id) {
      console.error('[DashboardContext] Cannot update portfolio settings: no active portfolio');
      throw new Error('No active portfolio selected');
    }

    try {
      const monitoredAxios = createMonitoredAxios('DashboardContext');

      const response = await monitoredAxios.put('/api/portfolio-settings', newSettings, {
        headers: {
          'X-Portfolio-ID': activePortfolio.id
        }
      });

      // Update local state with server response
      setPortfolioSettings(response.data);

      // Clear cache to ensure consistency across the dashboard
      clearCache(undefined, activePortfolio.id);

      return response.data;
    } catch (error) {
      console.error('[Dashboard] updatePortfolioSettings error:', error);
      throw error;
    }
  }, [activePortfolio]);

  // Initial data fetch when component mounts and portfolio is available
  // Don't fetch if auth is still loading - we need the token to be available
  // Use authLoadingRef to avoid stale closure issues in setTimeout
  useEffect(() => {
    if (activePortfolio && !authLoadingRef.current) {
      setLoading(true);
      const timer = setTimeout(() => {
        setLoading(true);
        // Check both activePortfolio AND authLoadingRef.current to ensure auth is ready
        if (activePortfolio && !authLoadingRef.current) {
          fetchComprehensiveBatchData();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [activePortfolio, fetchComprehensiveBatchData]);

  const value: DashboardContextType = {
    overview,
    assetProjection,
    cashflowProjection,
    recommendations,
    financialSummary,
    assets,
    categories,
    setCategories,
    transactions,
    income,
    expenses,
    subtitle,
    historicValues,
    portfolioSettings,
    chartData,
    loading: !activePortfolio ? false : loading, // Don't show loading state when no portfolio is selected
    dataLoaded: !!activePortfolio && loadedPortfolioId === activePortfolio.id,
    error: !activePortfolio ? null : error, // Clear error when no portfolio is selected
    includeDefault,
    projectionYears,
    refreshDashboard,
    fetchOverview,
    fetchAssetProjection,
    fetchRecommendations,
    fetchFinancialSummary,
    clearCache,
    setSubtitle: handleSetSubtitle,
    setIncludeDefault,
    setProjectionYears,
    setPortfolioSettings: updatePortfolioSettings,
    resetRetryState,
  };



  return (
    <DashboardContext.Provider value={value}>
      <DashboardCacheListener />
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboard() {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
}

// For components on public routes (e.g. /guides) that render with or without
// a DashboardProvider: returns undefined instead of throwing when absent.
export function useOptionalDashboard() {
  return useContext(DashboardContext);
}
